import React, { memo, useCallback, useEffect, useState } from "react";
import { ContactEmailRow, ContactPhoneRow, ContactAddressRow, ContactNoteRow } from "services/api/contacts/types";
import { getContactEmailsAPI, getContactPhonesAPI, getContactAddressesAPI, getContactNotesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { classNames } from "helpers";
import { Loader } from "ui/components/Loader";
import Badge from "ui/components/Badge/Badge";

import SummaryContactLine from "./components/SummaryContactLine";
import SummarySection from "./components/SummarySection";
import { CONTACT_SUMMARY_STEPS } from "./consts";
import { ContactSummaryStepProps } from "./types";

/** Inicijali za avatar bez slike (npr. "Orytron d.o.o." → "OD"). */
const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

/**
 * Pregled kontakta prije spremanja: profil s primarnim kontaktima, popunjenost po sekcijama i
 * sekcije s gumbom "Uredi" koji vodi ravno na korak čarobnjaka.
 */
const ContactSummaryStep: React.FC<ContactSummaryStepProps> = ({ formValues, onEditStep }) => {
  const contactId = formValues.id;
  const [emails, setEmails] = useState<ContactEmailRow[]>([]);
  const [phones, setPhones] = useState<ContactPhoneRow[]>([]);
  const [addresses, setAddresses] = useState<ContactAddressRow[]>([]);
  const [notes, setNotes] = useState<ContactNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (contactId) fetchAll();
  }, [contactId]);

  const fetchAll = async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const [emailRes, phoneRes, addressRes, noteRes] = await Promise.all([
        getContactEmailsAPI(initialDataWithPagination.pagination, contactId),
        getContactPhonesAPI(initialDataWithPagination.pagination, contactId),
        getContactAddressesAPI(initialDataWithPagination.pagination, contactId),
        getContactNotesAPI(initialDataWithPagination.pagination, contactId),
      ]);
      setEmails(emailRes.data);
      setPhones(phoneRes.data);
      setAddresses(addressRes.data);
      setNotes(noteRes.data);
    } catch {
    }
    setIsLoading(false);
  };

  const handleEditInfo = useCallback(() => {
    onEditStep(CONTACT_SUMMARY_STEPS.info);
  }, [onEditStep]);

  const isCompany = formValues.contactTypeId === "C";
  const name = (isCompany
    ? formValues.companyName
    : `${formValues.firstName ?? ""} ${formValues.lastName ?? ""}`.trim()) || "";

  const pictureFiles = (formValues.pictures ?? []).flatMap((picture) => picture.files);
  const avatarUrl = pictureFiles.find((file) => file.url)?.url;

  const primaryEmail = emails.find((email) => email.primary) ?? emails[0];
  const primaryPhone = phones.find((phone) => phone.primary) ?? phones[0];
  const primaryAddress = addresses.find((address) => address.primary) ?? addresses[0];

  // Popunjenost: koliko je sekcija uneseno (osnovni podaci su uvijek uneseni).
  const sections = [emails.length, phones.length, addresses.length, notes.length, pictureFiles.length];
  const filledCount = sections.filter((count) => count > 0).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  return (
    <div className="flex justify-center px-4 mt-8 xl:mt-12 pb-8">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pregled kontakta</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto">
          Provjerite unesene podatke. Klikom na <strong>"Spremi"</strong> kontakt je objavljen i vidljiv u sustavu.
        </p>

        {/* Profil */}
        <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
          <div className="h-16 bg-blue-50" />
          <div className="px-5 pb-5">
            {/* Samo avatar preklapa traku; ime i oznake su ispod nje. */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 min-w-0">
                <span className="-mt-8 flex items-center justify-center w-16 h-16 rounded-full border-4 border-white bg-blue-600 text-lg font-bold text-white overflow-hidden flex-shrink-0">
                  {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : getInitials(name)}
                </span>
                <div className="min-w-0 pt-3">
                  <div className="text-xl font-bold text-slate-900 truncate">{name || "Bez naziva"}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <Badge text={isCompany ? "Tvrtka" : "Osoba"} variant="info" height="small" />
                    {formValues.personalIdentificationNumber && (
                      <span className="font-mono">OIB {formValues.personalIdentificationNumber}</span>
                    )}
                    {!isCompany && formValues.jobTitle && <span>· {formValues.jobTitle}</span>}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleEditInfo}
                className="pt-3 text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0"
              >
                Uredi
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 rounded-sm bg-slate-50 p-3">
              <SummaryContactLine icon="/media/icons/email.svg" value={primaryEmail?.emailAddress} placeholder="Bez emaila" />
              <SummaryContactLine icon="/media/icons/phone.svg" value={primaryPhone?.phoneNumber} placeholder="Bez telefona" />
              <SummaryContactLine
                icon="/media/icons/location.svg"
                value={primaryAddress && [primaryAddress.line1, primaryAddress.city].filter(Boolean).join(", ")}
                placeholder="Bez adrese"
              />
            </div>

            {/* Popunjenost po sekcijama */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-600">Popunjenost</span>
                <span className="text-slate-400">
                  {filledCount} od {sections.length} sekcija
                </span>
              </div>
              <div className="mt-1.5 flex gap-1">
                {sections.map((count, index) => (
                  <span
                    key={index}
                    className={classNames("h-1.5 flex-1 rounded-full", count > 0 ? "bg-blue-500" : "bg-slate-200")}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sekcije */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <SummarySection
            title="Email adrese"
            count={emails.length}
            icon="/media/icons/email.svg"
            stepLabel={CONTACT_SUMMARY_STEPS.emails}
            onEditStep={onEditStep}
            emptyText="Nije unesena nijedna email adresa"
          >
            <div className="space-y-1.5">
              {emails.map((email) => (
                <div key={email.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="truncate">{email.emailAddress}</span>
                  {email.primary && <Badge text="Primarni" variant="info" height="small" />}
                </div>
              ))}
            </div>
          </SummarySection>

          <SummarySection
            title="Telefoni"
            count={phones.length}
            icon="/media/icons/phone.svg"
            stepLabel={CONTACT_SUMMARY_STEPS.phones}
            onEditStep={onEditStep}
            emptyText="Nije unesen nijedan telefon"
          >
            <div className="space-y-1.5">
              {phones.map((phone) => (
                <div key={phone.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="truncate">{phone.phoneNumber}</span>
                  {phone.primary && <Badge text="Primarni" variant="info" height="small" />}
                </div>
              ))}
            </div>
          </SummarySection>

          <SummarySection
            title="Adrese"
            count={addresses.length}
            icon="/media/icons/location.svg"
            stepLabel={CONTACT_SUMMARY_STEPS.addresses}
            onEditStep={onEditStep}
            emptyText="Nije unesena nijedna adresa"
          >
            <div className="space-y-2">
              {addresses.map((address) => (
                <div key={address.id} className="text-sm text-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{address.line1}</span>
                    {address.primary && <Badge text="Primarna" variant="info" height="small" />}
                  </div>
                  <div className="text-xs text-slate-400">
                    {[address.postalCode, address.city].filter(Boolean).join(" ")}
                    {address.country ? `, ${address.country}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </SummarySection>

          <SummarySection
            title="Bilješke"
            count={notes.length}
            icon="/media/icons/note.svg"
            stepLabel={CONTACT_SUMMARY_STEPS.notes}
            onEditStep={onEditStep}
            emptyText="Nema bilješki"
          >
            <div className="space-y-2">
              {notes.map((note) => (
                <div key={note.id} className="rounded-sm bg-slate-50 px-3 py-2">
                  <p className="text-sm text-slate-700 line-clamp-2">{note.content}</p>
                  {note.important && (
                    <div className="mt-1">
                      <Badge text="Važna bilješka" variant="warning" height="small" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </SummarySection>

          <div className="md:col-span-2">
            <SummarySection
              title="Slike"
              count={pictureFiles.length}
              icon="/media/icons/picture.svg"
              stepLabel={CONTACT_SUMMARY_STEPS.pictures}
              onEditStep={onEditStep}
              emptyText="Nema dodanih slika"
            >
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {pictureFiles.map((file, index) => (
                  <div
                    key={file.id || index}
                    className="aspect-square rounded-sm border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
                    title={file.name}
                  >
                    {file.url ? (
                      <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="px-1 text-[0.6rem] font-semibold uppercase text-slate-400 truncate">
                        {file.name?.split(".").pop()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </SummarySection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ContactSummaryStep);
